import { create } from "zustand";
import { toast } from "react-toastify";
import {
  postCrmPrepaidLink,
  getCrmPrepaidStatus,
  addCustomerAddress,
} from "../api";
import { useCustomer } from "./index";

// Poll cadence, plus the grace we keep polling past the link's own expiry so the final
// read still lands (order-service flips PENDING -> EXPIRED lazily, on read).
const POLL_INTERVAL_MS = 15000;
const POLL_GRACE_MS = 60 * 1000;
// Only used when the link-creation response carried no expiresAt. A fixed window is wrong
// as the primary rule: the configured expiryMinutes is often shorter, which left us polling
// a link that was already dead.
const FALLBACK_POLL_DURATION_MS = 10 * 60 * 1000;

// The session lives above the cart Offcanvas so the agent can minimise the modal, close
// the cart and keep working on the lead page while the customer pays. Timer handles are
// module-scoped (not state) — they must survive re-renders without triggering them.
let pollTimer = null;
let pollDeadline = null;

// phase: idle -> sending -> awaiting -> (paid | expired | cancelled | refunded | error | stalled)
// While locked the agent must not leave or reload the page — the payment result would be lost.
export const isSessionLocked = (phase) =>
  phase === "sending" || phase === "awaiting";

const INITIAL_STATE = {
  open: false,
  minimized: false,
  phase: "idle",
  linkRef: null,
  expiresAt: null,
  lastCheckedAt: null,
  channels: { waba: false, sms: false },
  orderDisplayId: null,
  orderStatus: null,
  // context captured when the session opens
  caseId: null,
  userId: null,
  selectedAddress: null,
  payload: null,
  customerDetails: null,
};

export const useCrmPrepaidSession = create((set, get) => ({
  ...INITIAL_STATE,

  openSession: ({ caseId, userId, selectedAddress, payload, customerDetails }) => {
    set({
      ...INITIAL_STATE,
      open: true,
      caseId,
      userId,
      selectedAddress,
      payload,
      customerDetails,
    });
  },

  minimize: () => set({ minimized: true }),
  restore: () => set({ minimized: false }),

  closeSession: () => {
    get().stopPolling();
    set({ ...INITIAL_STATE });
  },

  stopPolling: () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },

  applyStatus: async (data) => {
    const status = data?.status;
    if (!["paid", "expired", "cancelled", "refunded"].includes(status)) return;

    get().stopPolling();
    // Any terminal status pops the modal back in front of the agent, even if minimised.
    if (status === "paid") {
      set({
        phase: "paid",
        minimized: false,
        orderDisplayId: data.orderDisplayId || null,
        orderStatus: data.orderStatus || null,
      });
      // Refresh the case via the store action so `customerData` state actually updates
      // (the raw getCustomerData GET set nothing) and the CRM reflects the new order.
      try {
        await useCustomer.getState().fetchCustomerData(get().caseId);
      } catch (e) {
        // non-blocking: the modal already shows the order details
      }
    } else {
      set({ phase: status, minimized: false });
    }
  },

  startPolling: (ref) => {
    get().stopPolling();
    // Poll until the link's own expiry (+ grace), not a fixed window — past expiry there is
    // nothing left to wait for, and the last poll inside the grace picks up the EXPIRED flip.
    const expiresAtMs = get().expiresAt ? new Date(get().expiresAt).getTime() : NaN;
    pollDeadline = Number.isFinite(expiresAtMs)
      ? expiresAtMs + POLL_GRACE_MS
      : Date.now() + FALLBACK_POLL_DURATION_MS;
    pollTimer = setInterval(async () => {
      if (Date.now() > pollDeadline) {
        // Give up auto-checking, release the page guards so the agent isn't stuck in a tab
        // they can't close, navigate or refresh, and surface the modal — a minimised bar
        // still reading "Awaiting payment" would be lying, nothing is being checked now.
        get().stopPolling();
        set({ phase: "stalled", minimized: false });
        return;
      }
      try {
        const res = await getCrmPrepaidStatus(ref);
        await get().applyStatus(res?.data?.data || res?.data);
      } catch (e) {
        // transient poll error — keep trying until the cap
      }
    }, POLL_INTERVAL_MS);
  },

  sendLink: async () => {
    const { caseId, userId, selectedAddress, payload } = get();
    if (!selectedAddress || !userId) {
      toast.error("Missing address or customer. Please refresh and try again.");
      return;
    }
    try {
      set({ phase: "sending" });
      // 1) Persist ONLY a freshly-entered (local) address — mirrors handleConfirmAddress
      //    in the COD flow. An already-saved address is re-used server-side via
      //    getCustomerAddress; re-posting it would send a bare `phone` (stored split from
      //    its country code) and fail the /address country-code validation on repeat sends.
      const { _id, ...addressData } = selectedAddress;
      if (String(_id || "").startsWith("local_")) {
        await addCustomerAddress(userId, addressData);
      }

      // 2) Create the link + send on BOTH WhatsApp and SMS.
      const response = await postCrmPrepaidLink(caseId, payload);
      const ref = response?.data?.linkRef;
      const sentChannels = response?.data?.channels || { waba: true, sms: true };
      set({ channels: sentChannels });
      if (!ref) {
        throw new Error("No link reference returned");
      }
      set({ linkRef: ref, expiresAt: response?.data?.expiresAt || null, phase: "awaiting" });
      toast.success(response?.data?.message || "Payment link sent");
      get().startPolling(ref);
    } catch (error) {
      set({ phase: "error" });
      toast.error(
        error?.response?.data?.message || "Failed to send crm_prepaid link"
      );
    }
  },

  // Manual re-check from the `stalled` state. If the answer isn't terminal we STAY on the
  // stalled screen — flipping back to `awaiting` used to unmount the buttons, replace them
  // with an indefinite spinner and silently re-arm the page lock, so the click looked like
  // it had done nothing. The agent keeps the buttons and stays free to leave the page.
  checkStatusOnce: async () => {
    const ref = get().linkRef;
    if (!ref) return;
    try {
      const res = await getCrmPrepaidStatus(ref);
      await get().applyStatus(res?.data?.data || res?.data);
    } catch (e) {
      toast.error("Could not fetch the latest status. Please try again.");
      return;
    }
    if (get().phase === "stalled") {
      set({ lastCheckedAt: new Date().toISOString() });
      toast.info("Still awaiting payment — nothing has changed yet.");
    }
  },
}));

export default useCrmPrepaidSession;
